import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type ThemeMode = 'light' | 'dark';

/** Tailwind class tokens consumed by `components/ui`. */
export type ThemeColors = {
  background: string;
  surface: string;
  surfaceHover: string;
  headerBg: string;
  border: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  primary: string;
  primaryText: string;
  primaryFg: string;
  primaryBorder: string;
  primaryBorderAll: string;
  primaryChecked: string;
  primaryCheckedHover: string;
  accent: string;
  error: string;
  warning: string;
  info: string;
  success: string;
  interactiveHover: string;
};

/**
 * Semantic utility classes. Actual light/dark values come from CSS variables
 * on `:root` / `.dark` (see index.css + tailwind.config.js).
 */
const themeColors: ThemeColors = {
  background: 'bg-surface-card',
  surface: 'bg-surface-card',
  surfaceHover: 'bg-surface-muted',
  headerBg: 'bg-surface-muted',
  border: 'border-line',
  text: 'text-ink',
  textSecondary: 'text-ink-muted',
  textMuted: 'text-ink-faint',
  primary: 'bg-brand',
  primaryText: 'text-white',
  primaryFg: 'text-brand',
  primaryBorder: 'border-brand ring-brand',
  primaryBorderAll: 'border-brand',
  primaryChecked: 'peer-checked:bg-brand',
  primaryCheckedHover: 'peer-checked:hover:bg-brand-hover',
  accent: 'bg-accent',
  error: 'bg-red-600 text-white',
  warning: 'bg-amber-500 text-white',
  info: 'bg-sky-600 text-white',
  success: 'bg-emerald-600 text-white',
  interactiveHover: 'hover:bg-surface-muted',
};

const THEME_STORAGE_KEY = 'balanceflow_theme';

type ThemeContextValue = {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  colors: ThemeColors;
  isDark: boolean;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(): ThemeMode {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // ignore
  }
  if (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    return 'dark';
  }
  return 'light';
}

function applyDocumentTheme(theme: ThemeMode) {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.dataset.theme = theme;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'light';
    const initial = readStoredTheme();
    applyDocumentTheme(initial);
    return initial;
  });

  useEffect(() => {
    applyDocumentTheme(theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // ignore
    }
  }, [theme]);

  const setTheme = useCallback((next: ThemeMode) => {
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      colors: themeColors,
      isDark: theme === 'dark',
    }),
    [theme, setTheme, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}
